import React, { useState } from "react";
import {
  Flex,
  Text,
  Image,
  Skeleton,
  Center,
  Box,
  Grid,
  GridItem,
  Button,
  Link,
  useToast,
  Icon,
  HStack,
  VStack,
  Avatar,
} from "@chakra-ui/react";
import { FaEnvelope, FaCheck, FaCalendarAlt, FaRedo } from "react-icons/fa";
import {
  IoCalendarOutline,
  IoLocationOutline,
  IoPeopleOutline,
  IoTicketOutline,
  IoBagHandleOutline,
} from "react-icons/io5";
import { httpsCallable } from "firebase/functions";
import { functions } from "../../firebase/firebase-config";
import { ReactComponent as QRCode } from "../../Assets/SVG/qrcode.svg";
import GooglePlaySVG from "../../Assets/SVG/google_svg";
import AppStoreSVG from "../../Assets/SVG/appstore_svg";
import s1 from "../../Assets/Images/s1.png";
import sec1 from "../../Assets/Images/sec1.png";
import sec2 from "../../Assets/Images/sec2.png";
import sec3 from "../../Assets/Images/sec3.png";
import { stripHtml } from "../../Components/Structure/RichTextEditor";
import { nonHostAttendeeCount } from "../../lib/eventMoney";

// ─── Date/time helpers ────────────────────────────────────────────────────
const toDate = (ts) =>
  ts?.toDate ? ts.toDate() : ts ? new Date(ts) : null;
const fmtDate = (ts) => {
  const d = toDate(ts);
  if (!d) return null;
  return d.toLocaleDateString("en-CA", {
    weekday: "long",
    month: "long",
    day: "numeric",
    year: "numeric",
  });
};
const fmtTime = (ts) => {
  const d = toDate(ts);
  if (!d) return null;
  return d.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
};

export const ConfirmFloating = ({ event, user }) => {
  const [imageLoaded, setImageLoaded] = useState(false);
  const [resending, setResending] = useState(false);
  const [resent, setResent] = useState(false);
  const toast = useToast();

  const handleResendEmail = async () => {
    if (!event || !user) {
      toast({
        title: "Unable to resend",
        description: "Missing event or user information",
        status: "error",
        duration: 3000,
      });
      return;
    }

    setResending(true);
    try {
      const resendConfirmationEmail = httpsCallable(functions, "resendConfirmationEmail");
      await resendConfirmationEmail({ 
        eventId: event.id,
        userId: user.uid 
      });
      
      setResent(true);
      toast({
        title: "Email sent!",
        description: "Confirmation email has been resent",
        status: "success",
        duration: 3000,
      });
    } catch (error) {
      console.error("Error resending email:", error);
      toast({
        title: "Failed to resend",
        description: error.message || "Please try again later",
        status: "error",
        duration: 3000,
      });
    } finally {
      setResending(false);
    }
  };

  // ─── Event recap data ─────────────────────────────────────────────────
  const dateLabel = fmtDate(event?.startTime);
  const timeLabel = fmtTime(event?.startTime);
  const venueName = event?.partnerVenueName || null;
  const locationAddress = event?.location || null;
  const hasLocation = Boolean(venueName || locationAddress);
  const capacity = Number(event?.maxMembers) || 0;
  const baseAttendees = event ? nonHostAttendeeCount(event) : 0;
  // If the buyer's uid is already in event.members the Firestore write has
  // propagated back; use the count as-is. Otherwise add +1 optimistically.
  const buyerInMembers =
    user?.uid &&
    Array.isArray(event?.members) &&
    event.members.some((ref) => ref.id === user.uid);
  const attendees = buyerInMembers
    ? baseAttendees
    : Math.min(capacity > 0 ? capacity : Infinity, baseAttendees + 1);
  const spotsLeft = capacity > 0 ? Math.max(0, capacity - attendees) : null;
  const shortDateLabel = toDate(event?.startTime)?.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
  const descriptionPreview = event?.description
    ? stripHtml(event.description).trim()
    : "";
  const guestsToBring = event?.guestsToBring?.trim() || "";
  const heroImage =
    event?.imageUrl ||
    "https://do1pg20gb8r9m.cloudfront.net/siteV2/Proximy_Doorway_full.jpg";

  return (
    <Flex flexDirection={"column"} alignItems={"stretch"} w="100%">
      {/* ── Ticket-style modal content ─────────────────────────────────── */}
      <Box
        bg="white"
        borderRadius="24px"
        overflow="hidden"
        mb={0}
        border="1px solid"
        borderColor="gray.200"
        boxShadow="0 12px 30px rgba(0,0,0,0.12)"
        position="relative"
      >
        <Box position="relative">
          <Skeleton isLoaded={imageLoaded}>
            <Image
              src={heroImage}
              alt={event?.name || "Proximy Confirmation"}
              w="100%"
              h={["190px", "220px", "250px"]}
              objectFit="cover"
              onLoad={() => setImageLoaded(true)}
            />
          </Skeleton>
          <Box
            position="absolute"
            inset={0}
            bgGradient="linear(to-t, blackAlpha.750 0%, blackAlpha.300 52%, transparent 100%)"
          />
          <HStack
            position="absolute"
            top={3}
            left={3}
            spacing={1.5}
            bg="white"
            borderRadius="full"
            px={3}
            py={1}
            boxShadow="sm"
          >
            <Icon as={IoTicketOutline} color="#508CF7" boxSize="13px" />
            <Text fontSize="10px" fontWeight="900" color="#508CF7" letterSpacing="0.6px">
              CONFIRMED
            </Text>
          </HStack>
        </Box>

        <Box px={[5, 6]} pt={6} pb={5} position="relative">
          <Text
            fontSize={["20px", "22px"]}
            fontWeight="900"
            color="gray.900"
            textAlign="center"
            lineHeight="1.25"
            className="font-gilroy-bold"
          >
            {event?.name || "Your Hangout"}
          </Text>
          {dateLabel && (
            <Text
              fontSize="15px"
              fontWeight="800"
              color="gray.800"
              textAlign="center"
              mt={1}
            >
              {dateLabel}
            </Text>
          )}

          {event?.memberData?.displayName && (
            <HStack justify="center" spacing={1.5} mt={3}>
              <Avatar
                size="2xs"
                src={event?.memberData?.avatarUrl}
                name={event.memberData.displayName}
              />
              <Text fontSize="12px" fontWeight="700" color="gray.500">
                Hosted by {event.memberData.displayName}
              </Text>
            </HStack>
          )}

          <Box borderTop="1px dashed" borderColor="gray.300" my={5} />

          {/* Ticket notch cutouts */}
          <Box
            position="absolute"
            left="-18px"
            top="100px"
            boxSize="36px"
            bg="white"
            border="1px solid"
            borderColor="gray.200"
            borderLeft="0"
            borderRadius="full"
          />
          <Box
            position="absolute"
            right="-18px"
            top="100px"
            boxSize="36px"
            bg="white"
            border="1px solid"
            borderColor="gray.200"
            borderRight="0"
            borderRadius="full"
          />

          <Grid templateColumns="repeat(2, 1fr)" rowGap={4} columnGap={5}>
            <Box>
              <Text fontSize="13px" fontWeight="700" color="gray.400">
                Date
              </Text>
              <Text fontSize="14px" fontWeight="800" color="gray.900" mt={1}>
                {shortDateLabel || "TBA"}
              </Text>
            </Box>
            <Box textAlign="right">
              <Text fontSize="13px" fontWeight="700" color="gray.400">
                Time
              </Text>
              <Text fontSize="14px" fontWeight="800" color="gray.900" mt={1}>
                {timeLabel || "TBA"}
              </Text>
            </Box>

            <Box>
              <Text fontSize="13px" fontWeight="700" color="gray.400">
                Venue
              </Text>
              <Text fontSize="14px" fontWeight="800" color="gray.900" mt={1} noOfLines={2}>
                {venueName || locationAddress || "TBA"}
              </Text>
            </Box>
            <Box textAlign="right">
              <Text fontSize="13px" fontWeight="700" color="gray.400">
                Guests
              </Text>
              <Text fontSize="14px" fontWeight="800" color="gray.900" mt={1}>
                {capacity > 0 ? `${attendees} / ${capacity}` : attendees}
              </Text>
              {spotsLeft !== null && spotsLeft <= 5 && spotsLeft > 0 && (
                <Text fontSize="11px" fontWeight="800" color="#508CF7">
                  {spotsLeft} spot{spotsLeft !== 1 ? "s" : ""} left
                </Text>
              )}
            </Box>
          </Grid>

          {(guestsToBring || descriptionPreview) && (
            <>
              <Box borderTop="1px dashed" borderColor="gray.300" my={5} />

              {guestsToBring && (
                <Box mb={descriptionPreview ? 4 : 0}>
                  <HStack spacing={2} mb={1}>
                    <Icon as={IoBagHandleOutline} color="#508CF7" boxSize="16px" />
                    <Text fontSize="12px" fontWeight="900" color="gray.500">
                      WHAT TO BRING
                    </Text>
                  </HStack>
                  <Text fontSize="14px" color="gray.900" fontWeight="700" lineHeight="1.5">
                    {guestsToBring}
                  </Text>
                </Box>
              )}

              {descriptionPreview && (
                <Box>
                  <Text fontSize="12px" fontWeight="900" color="gray.500" mb={1}>
                    ABOUT THIS HANGOUT
                  </Text>
                  <Text fontSize="13px" color="gray.700" lineHeight="1.55" noOfLines={4}>
                    {descriptionPreview}
                  </Text>
                </Box>
              )}
            </>
          )}

          <Box borderTop="1px dashed" borderColor="gray.300" my={5} />

          <HStack justify="space-between" align="center">
            <HStack spacing={3}>
              <Flex
                w="38px"
                h="38px"
                borderRadius="10px"
                bg={resent ? "green.100" : "#F3F8FE"}
                alignItems="center"
                justifyContent="center"
              >
                <Icon
                  as={resent ? FaCheck : FaEnvelope}
                  color={resent ? "green.500" : "#508CF7"}
                  boxSize={4}
                />
              </Flex>
              <VStack align="start" spacing={0}>
                <Text fontSize="sm" fontWeight="800" color="gray.800">
                  {resent ? "Email Sent" : "Confirmation Email"}
                </Text>
                <Text fontSize="xs" color="gray.500" fontWeight="600">
                  {event?.id || "PROXIMY"} · {resent ? "Check your inbox" : "Includes calendar invite"}
                </Text>
              </VStack>
            </HStack>
            <Button
              size="sm"
              variant={resent ? "ghost" : "solid"}
              bg={resent ? "transparent" : "#508CF7"}
              color={resent ? "green.500" : "white"}
              _hover={{ bg: resent ? "transparent" : "#2a6ce2" }}
              leftIcon={<Icon as={resent ? FaCheck : FaRedo} boxSize={3} />}
              onClick={handleResendEmail}
              isLoading={resending}
              loadingText="Sending"
              isDisabled={resent}
              borderRadius="8px"
              fontSize="xs"
            >
              {resent ? "Sent" : "Resend"}
            </Button>
          </HStack>
        </Box>
      </Box>
      {/*
        ─── App download promo (hidden for now) ────────────────────────────
        The "What's Next" QR code, store buttons, and "Why Download the App"
        sections are commented out while the mobile app push is on hold.
        Restore by uncommenting this block.

      <Text
        fontSize="xl"
        fontWeight="bold"
        mb={2}
        w={"100%"}
        textAlign={"left"}
        className="font-gilroy-bold"
      >
        What's Next
      </Text>
      <Grid templateColumns="25% 60%" gap={4} w={"100%"} alignItems={"center"}>
        <Center>
          <QRCode />
        </Center>
        <Box>
          <Text
            color={"blackAlpha.700"}
            fontWeight={700}
            textAlign={"left"}
            fontSize={"16"}
            pt={"10px"}
            pb={"5px"}
          >
            Download The App
          </Text>

          <Text
            color={"blackAlpha.700"}
            textAlign={"left"}
            fontSize={"14px"}
            pt={"5px"}
            fontWeight="500"
            pb={"5px"}
          >
            Scan this QR Code to get redirected to the app on your phone
          </Text>
        </Box>
      </Grid>

      <Grid
        templateColumns="repeat(2, 1fr)"
        gap={4}
        pt={"20px"}
        pb={"30px"}
        w={"full"}
      >
        <Button
          as={Link}
          rounded={"xl"}
          href={
            "https://play.google.com/store/apps/details?id=com.proximy.proximyapp"
          }
          target="_blank"
          p={8}
          bg={"#508CF7"}
          _active={{ bg: "buttons.selected" }}
          _hover={{ bg: "#2a6ce2" }}
        >
          <GooglePlaySVG />
        </Button>

        <Button
          as={Link}
          rounded={"xl"}
          href={"https://apps.apple.com/us/app/proximy/id1563596886"}
          target="_blank"
          p={8}
          bg={"#508CF7"}
          _hover={{ bg: "#2a6ce2" }}
        >
          <AppStoreSVG />
        </Button>
      </Grid>

      <Text
        fontSize="xl"
        fontWeight="bold"
        mb={3}
        w={"100%"}
        textAlign={"left"}
        className="font-gilroy-bold"
      >
        Why Download the App
      </Text>
      <Grid
        templateColumns={["1fr", "40% 60%"]}
        columnGap={4}
        mb={[6, 3]}
        w={"100%"}
        alignItems={"center"}
      >
        <Image
          src={sec1}
          alt="Proximy Confirmation"
          borderRadius="10px"
          w="100%"
          objectFit="cover"
          onLoad={() => setImageLoaded(true)}
        />
        <Box mr={"40px"}>
          <Text
            color={"black"}
            fontWeight={700}
            textAlign={"left"}
            fontSize={"16"}
            pt={"10px"}
            pb={"5px"}
          >
            View Hangouts that match your Interests
          </Text>

          <Text
            color={"blackAlpha.700"}
            textAlign={"left"}
            fontSize={"14px"}
            pt={"5px"}
            fontWeight="500"
            pb={"5px"}
          >
            Discover events curated around what you love — from music and art to
            tech and wellness.
          </Text>
        </Box>
      </Grid>
      <Grid
        templateColumns={["1fr", "40% 60%"]}
        columnGap={4}
        w={"100%"}
        mb={[6, 3]}
        alignItems={"center"}
      >
        <Image
          src={sec3}
          alt="Proximy Confirmation"
          borderRadius="10px"
          w="100%"
          objectFit="cover"
          onLoad={() => setImageLoaded(true)}
        />
        <Box mr={"40px"}>
          <Text
            color={"black"}
            fontWeight={700}
            textAlign={"left"}
            fontSize={"16"}
            pt={"10px"}
            pb={"5px"}
          >
            Get to know the people going to the Hangout with you
          </Text>

          <Text
            color={"blackAlpha.700"}
            textAlign={"left"}
            fontSize={"14px"}
            pt={"5px"}
            fontWeight="500"
            pb={"5px"}
          >
            See who's attending ahead of time so you can break the ice and make
            meaningful connections.
          </Text>
        </Box>
      </Grid>

      <Grid
        templateColumns={["1fr", "40% 60%"]}
        columnGap={4}
        w={"100%"}
        mb={[6, 3]}
        alignItems={"center"}
      >
        <Image
          src={s1}
          alt="Proximy Confirmation"
          borderRadius="10px"
          w="100%"
          objectFit="cover"
          onLoad={() => setImageLoaded(true)}
        />
        <Box mr={"40px"}>
          <Text
            color={"black"}
            fontWeight={700}
            textAlign={"left"}
            fontSize={"16"}
            pt={"10px"}
            pb={"5px"}
          >
            Create your own Hangouts
          </Text>

          <Text
            color={"blackAlpha.700"}
            textAlign={"left"}
            fontSize={"14px"}
            pt={"5px"}
            fontWeight="500"
            pb={"5px"}
          >
            Host events around your interests, invite your community, and bring
            people together your way.
          </Text>
        </Box>
      </Grid>
        ──────────────────────────────────────────────────────────────────── */}
    </Flex>
  );
};
